'use client';

import { c as _c } from "react/compiler-runtime";
import type { Fetchable } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { formatLongString } from '@morpho-org/morpho-ts';
import { Avatar, NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { formatters } from '@morpho-org/ui-v6/utils';
import { TruncatedTooltip } from '@repo/ui';
import type { ForceDeallocationContext, InKindRedemptionFeeContext } from '@/components/common/VaultV2FeeRow';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { TransactionInfoCard } from '@/components/common/TransactionInfoCard';
import VaultV2FeeRow from '@/components/common/VaultV2FeeRow';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { formatAdjustedAmount, getParsedValue } from '@/helpers/formatters';
import { isVaultV2RateCapped } from '@/helpers/vaultV2RateCap';
import { useVaultV2Apy } from '@/hooks/useVaultV2Apy';
import type { VaultV2PositionManagerData } from './toVaultV2PositionManagerData';
import { IconRewardHover } from '../../IconRewardHover';
interface VaultV2TransactionInfoCardProps {
  vaultData: VaultV2PositionManagerData;
  initialPosition: Fetchable<bigint>;
  finalPosition: Fetchable<bigint>;
  feeAssets?: bigint;
  forceDeallocation?: ForceDeallocationContext;
  inKindRedemption?: InKindRedemptionFeeContext;
}
const VaultV2TransactionInfoCard = t0 => {
  const $ = _c(64);
  const {
    vaultData,
    initialPosition,
    finalPosition,
    feeAssets,
    forceDeallocation,
    inKindRedemption
  } = t0;
  const chainId = vaultData.asset.chain.id;
  let t1;
  if ($[0] !== chainId) {
    t1 = InterfaceChainUtils.getMetadata(chainId);
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chain = t1;
  const vaultAsset = getParsedValue(vaultData.asset, parseApiAsset);
  let t2;
  if ($[2] !== vaultData) {
    t2 = {
      vaultV2: vaultData
    };
    $[2] = vaultData;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    apy,
    netApy,
    performanceFee,
    managementFee,
    points,
    rewards,
    maxRate
  } = useVaultV2Apy(t2);
  const vaultName = vaultData.name ?? "";
  let t3;
  if ($[4] !== netApy) {
    t3 = formatters.percent.digits(2).default("-").unit("%").max(10).of(netApy, 18);
    $[4] = netApy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedNetApy = t3;
  const {
    totalAssetsUsd,
    collateralExposures
  } = vaultData;
  if (!vaultAsset) {
    return null;
  }
  let t4;
  if ($[6] !== vaultName) {
    t4 = ref => <span ref={ref} className="text-title-xs min-w-0 truncate">{vaultName}</span>;
    $[6] = vaultName;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4 || $[9] !== vaultName) {
    t5 = <TruncatedTooltip text={vaultName}>{t4}</TruncatedTooltip>;
    $[8] = t4;
    $[9] = vaultName;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = TransactionInfoCard;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      size: "small"
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== chain.chain.id) {
    t8 = <Network chainId={chain.chain.id} srcAvatarProps={t7} />;
    $[12] = chain.chain.id;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== chain.chain.name) {
    t9 = <span className="text-3xs">{chain.chain.name}</span>;
    $[14] = chain.chain.name;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <TransactionInfoCard.ListItem label="Network" value={<div className="gap-2xs flex flex-row items-center">{t8}{t9}</div>} />;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = TransactionInfoCard;
  const t12 = `My Position (${formatLongString(vaultAsset.symbol ?? "")})`;
  let t13;
  if ($[19] !== initialPosition) {
    t13 = {
      value: initialPosition
    };
    $[19] = initialPosition;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== finalPosition) {
    t14 = {
      value: finalPosition
    };
    $[21] = finalPosition;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== vaultAsset.decimals || $[24] !== vaultAsset.price) {
    t15 = collateral => formatAdjustedAmount({
      price: vaultAsset.price,
      amount: collateral,
      decimals: vaultAsset.decimals,
      withTrailingZero: false
    });
    $[23] = vaultAsset.decimals;
    $[24] = vaultAsset.price;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t14 || $[28] !== t15) {
    t16 = <TransactionInfoCard.ValueChange initialProps={t13} finalProps={t14} formatter={t15} />;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t11.ListItem || $[31] !== t12 || $[32] !== t16) {
    t17 = <t11.ListItem label={t12} value={t16} />;
    $[30] = t11.ListItem;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== apy || $[35] !== formattedNetApy || $[36] !== managementFee || $[37] !== maxRate || $[38] !== netApy || $[39] !== performanceFee || $[40] !== points || $[41] !== rewards || $[42] !== vaultAsset || $[43] !== vaultData.address || $[44] !== vaultData.asset.chain.id || $[45] !== vaultData.grossApy || $[46] !== vaultData.maxApy || $[47] !== vaultData.netApy) {
    t18 = vaultData.netApy !== null && <TransactionInfoCard.ListItem data-testid="vault-v2-transaction-info-apy" label="APY" value={<div className="flex flex-row items-center gap-[2px]"><IconRewardHover RewardsHoverCardProps={{
        performanceFee,
        managementFee,
        apy,
        rewards,
        points,
        variant: "vault",
        asset: vaultAsset,
        netApy,
        maxRate,
        maxApy: vaultData.maxApy,
        isRateCapped: isVaultV2RateCapped(vaultData.grossApy, vaultData.maxApy),
        includesDonations: vaultIncludesDonations(vaultData.asset.chain.id, vaultData.address)
      }} vaultV2ApyInput={{
        chainId: vaultData.asset.chain.id,
        address: vaultData.address
      }}><TransactionInfoCard.Value value={formattedNetApy} /></IconRewardHover></div>} />;
    $[34] = apy;
    $[35] = formattedNetApy;
    $[36] = managementFee;
    $[37] = maxRate;
    $[38] = netApy;
    $[39] = performanceFee;
    $[40] = points;
    $[41] = rewards;
    $[42] = vaultAsset;
    $[43] = vaultData.address;
    $[44] = vaultData.asset.chain.id;
    $[45] = vaultData.grossApy;
    $[46] = vaultData.maxApy;
    $[47] = vaultData.netApy;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== collateralExposures || $[50] !== totalAssetsUsd) {
    t19 = collateralExposures.length > 0 && <TransactionInfoCard.ListItem data-testid="vault-v2-transaction-info-exposure" label="Exposure" value={<CollateralExposureStack align="right" collateralExposures={collateralExposures} maxTokensCount={5} totalAssetsUsd={totalAssetsUsd} />} />;
    $[49] = collateralExposures;
    $[50] = totalAssetsUsd;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== feeAssets || $[53] !== forceDeallocation || $[54] !== inKindRedemption || $[55] !== vaultAsset) {
    t20 = feeAssets !== undefined && feeAssets > ZERO && <VaultV2FeeRow className="w-full" feeAssets={feeAssets} forceDeallocation={forceDeallocation} inKindRedemption={inKindRedemption} vaultAsset={vaultAsset} />;
    $[52] = feeAssets;
    $[53] = forceDeallocation;
    $[54] = inKindRedemption;
    $[55] = vaultAsset;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t10 || $[58] !== t17 || $[59] !== t18 || $[60] !== t19 || $[61] !== t20 || $[62] !== t6.List) {
    t21 = <t6.List>{t10}{t17}{t18}{t19}{t20}</t6.List>;
    $[57] = t10;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
    $[62] = t6.List;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  return <TransactionInfoCard.Root><TransactionInfoCard.Header><Avatar size="medium" src={vaultAsset.logoURI} />{t5}</TransactionInfoCard.Header>{t21}</TransactionInfoCard.Root>;
};
export default VaultV2TransactionInfoCard;